import classNames from 'classnames';
import type { ReactElement, RefObject, Ref, CSSProperties } from 'react';
import { useEffect, useRef, useCallback } from 'react';
import type { LocationDropdownProps, LocationDropdownRef } from './LocationDropdown.data';
import styles from './LocationDropdown.module.scss';
import {
  setupTransitionInTimeline,
  // setupTransitionOutTimeline,
  setupMouseEnterTimeline,
  setupMouseLeaveTimeline,
  toggle,
  toggleIconState,
} from './LocationDropdown.transitions';
import { useScrollTransition, useTransitionController } from 'util/react-transition-component';
import { useGlobalStore } from '../../../stores/global';

import Icon from '../../atoms/icon/Icon';
import Heading from '../../atoms/heading/Heading';
import LocationItem from './LocationItem';

import { useTimeout } from '../../../hooks/useTimeout';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import useMount from '../../../hooks/useMount';
import { useIsDesktopDevice } from '../../../hooks/useIsDesktopDevice';
import useToggle from '../../../hooks/useToggle';

import type { HeadingRef } from '../../atoms/heading/Heading.data';

import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import PrimaryLink from '../../atoms/primary-link/PrimaryLink';

export default function LocationDropdown({
  className,
  region,
  iconColor,
  link,
  region_link_label,
  ref,
}: LocationDropdownProps & { ref?: Ref<LocationDropdownRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const listRef = useRef<HTMLUListElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const iconPlusRef = useRef<HTMLDivElement>(null);
  const iconMinusRef = useRef<HTMLDivElement>(null);

  const headingRef = useRef<HeadingRef>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const isDesktopDevice = useIsDesktopDevice(false);
  const lineRef = useRef<HTMLSpanElement>(null);

  const [isOpen, toggleOpen] = useToggle(false);

  const { locationsList } = useGlobalStore();

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        headingRef,
        lineRef,
        iconRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const { start: refreshScrollTriggerTimeout } = useTimeout(
    () => {
      ScrollTrigger.refresh();
    },
    0,
    false,
  );

  useUpdateEffect(() => {
    if (listRef.current) {
      toggle(listRef, isOpen, refreshScrollTriggerTimeout, false);
    }
  }, [isOpen]);

  useMount(() => {
    if (!isOpen && listRef.current) {
      toggle(listRef, isOpen, refreshScrollTriggerTimeout, true);
    }
  });

  const hoverTransitionController = useTransitionController(
    () => ({
      refs: {
        backgroundRef,
        headingRef,
        iconRef,
      },
      setupTransitionInTimeline: setupMouseEnterTimeline,
      setupTransitionOutTimeline: setupMouseLeaveTimeline,
    }),
    [],
  );

  useEffect(() => {
    if (isOpen) {
      hoverTransitionController.transitionOut();
    }
    toggleIconState(
      {
        iconRef,
        iconPlusRef,
        iconMinusRef,
      },
      isOpen,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onMouseEnter = useCallback(() => {
    if (!isOpen && isDesktopDevice) {
      hoverTransitionController.transitionIn();
    }
  }, [hoverTransitionController, isOpen, isDesktopDevice]);

  const onMouseLeave = useCallback(() => {
    if (!isOpen && isDesktopDevice) {
      hoverTransitionController.transitionOut();
    }
  }, [hoverTransitionController, isOpen, isDesktopDevice]);

  const dropdownStyle = iconColor ? ({ '--accent-color': iconColor } as CSSProperties) : undefined;

  return (
    <div ref={elementRef} className={className} style={dropdownStyle}>
      <button
        type="button"
        className={classNames(styles.container, 'button')}
        onClick={toggleOpen}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div ref={backgroundRef} className={classNames(styles.background, 'abs-fill')} />
        <span ref={lineRef} className={styles.line} />

        <div className={styles.regionContainer}>
          <Heading
            as="h2"
            size="heading-01"
            className={classNames(styles.region)}
            ref={headingRef}
            transitionInType="splitCharsSlideInUp"
          >
            {region}
          </Heading>
          <Icon ref={iconPlusRef} name="plus" className={styles.hidden} />
          <Icon ref={iconMinusRef} name="minus" className={styles.hidden} />
          <Icon ref={iconRef} name="plus" className={styles.icon} />
        </div>

        {link?.cached_url && (
          <PrimaryLink
            textVariant="label-small"
            className={classNames(styles.link)}
            link={{ href: link.cached_url }}
            label={region_link_label}
            disableTriggerInOnScroll={false}
            transitionInType="splitCharsSlideInUp"
            target={link.target}
            onClick={(e) => e?.stopPropagation()}
          />
        )}
      </button>

      <ul ref={listRef} className={classNames(styles.list)}>
        {locationsList
          ?.filter((l) => l.region.includes(region))
          .sort((a, b) => a.city.localeCompare(b.city))
          .map((location, i) => (
            <LocationItem
              key={location._uid}
              className={classNames(styles.cityContainer)}
              location={location}
              isOpen={isOpen}
              index={i}
            />
          ))}
      </ul>
    </div>
  );
}
